/**
 * This component is designed to be used as top level App wrapper. It connects
 * itself to axios as response interceptor and checks error responses.
 */

import { useLocation } from '@reach/router';
import { pipe } from 'lodash/fp';
import T from 'prop-types';
import { useEffect, useReducer } from 'react';

function NetworkInterceptor(props) {
	const { forbidNavigation, renderError, renderSuccess } = props;

	// simple setState would work here, useReducer is micro-optimization which translates all falsy
	// values to undefined, thus values like `null` or `false` does not cause state change.
	const [customScreen, setCustomScreen] = useReducer(
		(_, newScreen) => newScreen || undefined,
		undefined
	);
	useEffect(() => {
		const errorListener = renderError && pipe(renderError, setCustomScreen);
		const successListener = renderSuccess && pipe(renderSuccess, setCustomScreen);

		window.addEventListener('axios-response', (event) => {
			if (successListener) {
				successListener(event.detail);
			}
		});

		window.addEventListener('axios-error', (event) => {
			if (errorListener) {
				errorListener(event.detail);
			}
		});

		return () => {
			window.removeEventListener('axios-response', successListener);
			window.removeEventListener('axios-error', errorListener);
		};
	}, [renderError, renderSuccess]);

	// Change of pathname resets customScreen, thus customScreen could contain links to static pages.
	// If you need to "lock" the user on customScreen, set `forbidNavigation` prop.
	const { pathname } = useLocation();
	useEffect(() => {
		if (!forbidNavigation) {
			setCustomScreen(undefined);
		}
	}, [pathname]);

	return customScreen || props.children;
}

NetworkInterceptor.propTypes = {
	/**
	 * Navigation away from the `render*` page is permitted by default. If an escape from the page is not desired, set
	 * this property to `true`.
	 */
	forbidNavigation: T.bool,
	/** Function deriving component do be rendered on successful response */
	renderSuccess: T.func,
	/** Function deriving component do be rendered on network error */
	renderError: T.func,
};

export default NetworkInterceptor;
